import React, { ReactNode } from 'react';

// ============================================================================
// EffectiveSummary - Shows the computed effective values with source indicators
// Used by: SitePricingFormV2, SiteOverageChargesForm, SiteFeesFormV2
// ============================================================================

export type EffectiveSource = 'store' | 'site';

export interface EffectiveItem<T> {
  data: T;
  source: EffectiveSource;
  label?: string;
}

export interface EffectiveSummaryProps<T> {
  title: string;
  items: EffectiveItem<T>[];
  renderItem: (item: T, source: EffectiveSource) => ReactNode;
  emptyMessage?: string;
  description?: string;
  highlightedValue?: ReactNode;
  highlightedLabel?: string;
  highlightedSource?: EffectiveSource;
}

function EffectiveSummary<T>({
  title,
  items,
  renderItem,
  emptyMessage = 'No effective values.',
  description,
  highlightedValue,
  highlightedLabel,
  highlightedSource,
}: EffectiveSummaryProps<T>) {
  return (
    <div className="effective-summary">
      <div className="effective-summary-header">
        <h4 className="effective-summary-title">
          <span className="effective-icon">⚡</span>
          {title}
        </h4>
      </div>

      {description && (
        <p className="effective-summary-description">{description}</p>
      )}

      {/* Optional highlighted main value (e.g., current effective rate) */}
      {highlightedValue && (
        <div className="effective-highlighted">
          <span className="effective-highlighted-value">{highlightedValue}</span>
          {highlightedLabel && (
            <span className="effective-highlighted-label">
              {highlightedLabel}
              {highlightedSource && (
                <span className={`source-badge ${highlightedSource}`}>
                  {highlightedSource === 'store' ? 'Store Default' : 'Site Specific'}
                </span>
              )}
            </span>
          )}
        </div>
      )}

      {/* List of effective items */}
      {items.length === 0 ? (
        <p className="effective-empty-message">{emptyMessage}</p>
      ) : (
        <div className="effective-items-list">
          {items.map((item, index) => (
            <div key={index} className={`effective-item effective-item-${item.source}`}>
              <span className={`source-indicator ${item.source}`}>
                {item.source === 'store' ? '○' : '●'}
              </span>
              <div className="effective-item-content">
                {renderItem(item.data, item.source)}
              </div>
              <span className={`source-badge-small ${item.source}`}>
                {item.source === 'store' ? 'Store' : 'Site'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default EffectiveSummary;

// ============================================================================
// Helper function to compute effective items
// ============================================================================

/**
 * Computes effective items by merging store defaults with site overrides.
 * Site items override store items when they match by a given key.
 *
 * @param storeItems - Array of store-level items
 * @param siteItems - Array of site-level items
 * @param getKey - Function to extract the matching key from an item
 * @returns Array of EffectiveItem with source indicators
 */
export function computeEffectiveItems<TStore, TSite, TKey>(
  storeItems: TStore[],
  siteItems: TSite[],
  getStoreKey: (item: TStore) => TKey,
  getSiteKey: (item: TSite) => TKey
): EffectiveItem<TStore | TSite>[] {
  const effective: EffectiveItem<TStore | TSite>[] = [];
  const siteKeysUsed = new Set<TKey>();

  // First, process store items and check for site overrides
  for (const storeItem of storeItems) {
    const key = getStoreKey(storeItem);
    const siteOverride = siteItems.find(s => getSiteKey(s) === key);

    if (siteOverride) {
      // Site override exists - use it
      effective.push({ data: siteOverride, source: 'site' });
      siteKeysUsed.add(key);
    } else {
      // No override - use store default
      effective.push({ data: storeItem, source: 'store' });
    }
  }

  // Add any site items that don't have a store counterpart
  for (const siteItem of siteItems) {
    const key = getSiteKey(siteItem);
    if (!siteKeysUsed.has(key)) {
      effective.push({ data: siteItem, source: 'site' });
    }
  }

  return effective;
}

/**
 * Computes effective items using ADDITIVE model (both store and site apply).
 * Used for fees where store fees always apply and site fees are additional.
 *
 * @param storeItems - Array of store-level items
 * @param siteItems - Array of site-level items
 * @returns Array of EffectiveItem with all items included
 */
export function computeAdditiveEffectiveItems<TStore, TSite>(
  storeItems: TStore[],
  siteItems: TSite[]
): EffectiveItem<TStore | TSite>[] {
  const effective: EffectiveItem<TStore | TSite>[] = [];

  // All store items apply
  for (const storeItem of storeItems) {
    effective.push({ data: storeItem, source: 'store' });
  }

  // All site items are additional
  for (const siteItem of siteItems) {
    effective.push({ data: siteItem, source: 'site' });
  }

  return effective;
}
